import { DefaultOptionType } from 'antd/es/select';
import { Dayjs } from 'dayjs';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FieldValues, FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { BaseButton } from '@/components/base-button/BaseButton';
import { FormInputDate } from '@/components/form-input-date/FormInputDate';
import { FormSelect } from '@/components/form-select/FormSelect';

import useAuthorization from '@/hooks/useAuthorization';
import useCompany from '@/hooks/useCompany';
import useFetch from '@/hooks/useFetch';
import useMotPic from '@/hooks/useMotPic';
import useOptionsGlobal from '@/hooks/useOptionsGlobal';

import { API } from '@/utils/constants/Apis';
import { DataInput } from '@/utils/helpers/common';
import { convertDataToOptions } from '@/utils/helpers/projectList';
import { Ii18n } from '@/utils/interfaces/i18n';
import { ICategory } from '@/utils/interfaces/masterdata';

import { StatusCategory } from '../task/models';
import { DEFAULT_FILTER_FORM, DEFAULT_FILTER_FORM_EXTERNAL, TYPE_OF_TASK } from './constant';

interface IFilterProps {
  defaultValues: any;
  onSubmit: (values: FieldValues) => void;
  onReset?: () => void;
}

const PrimaryFormFilter = ({ defaultValues, onSubmit, onReset }: IFilterProps) => {
  const form = useForm({
    defaultValues: DEFAULT_FILTER_FORM.primary
  });
  const isFirstLoadRef = useRef(true);
  const [motPicOption, setMotPicOption] = useState<DefaultOptionType[]>([]);
  const { watch } = useFormContext(); // retrieve all hook methods
  const { t }: Ii18n = useTranslation();
  const { isInternalRole } = useAuthorization();
  const { customCountryOptions, visaCategoriesGroupOption, filterOption } = useOptionsGlobal();
  const watchPicBy = watch('picBy');
  const watchCountryCode = form.watch('countryIds');
  const createDateTo = form.watch('createDateTo');
  const createDateFrom = form.watch('createDateFrom');
  const departureTo = form.watch('departureTo');
  const departureFrom = form.watch('departureFrom');
  const [searchValue, setSearchValue] = useState('');
  const { loading: loadingMotPic, userMotPicOptions } = useMotPic();
  const { companyOptions, loadingCompanyForAccount: loadingCompany } = useCompany();
  const { data: statusData, loading: loadingStatus } = useFetch<any[]>('/prj/status', 'GET');
  const { data: locationDetail, loading: loadingLocation } = useFetch<any[]>('/prj/projectmemos/get-all-location', 'GET');
  const locationOptions = useMemo(() => {
    const locations = locationDetail
      ?.map((item) => item?.location)
      .filter((location): location is string => typeof location === 'string' && Boolean(location.trim()));

    return Array.from(new Set(locations)).map((location) => ({ label: location, value: location }));
  }, [locationDetail]);
  const { data: categoryData } = useFetch<ICategory[] | null>(
    watchCountryCode?.length ? API.GET_CATEGORY(watchCountryCode, 'PROJECT', '') : '',
    'GET'
  );

  const categoryOptions = useMemo(() => {
    if (!categoryData || !watchCountryCode?.length) return [];

    return watchCountryCode
      .map((countryId) => {
        const countryGroup = visaCategoriesGroupOption.find((group) => group.id === countryId);
        if (!countryGroup) return null;

        const countryCategoryData = categoryData.filter((category: ICategory) => category.countryId === countryId);

        return {
          ...countryGroup,
          options: countryCategoryData.map((category: ICategory) => ({
            label: category.name,
            value: category.id
          }))
        };
      })
      .filter((item): item is Exclude<typeof item, null> => item !== null);
  }, [categoryData, watchCountryCode, visaCategoriesGroupOption]);

  const filteredCategoryOptions = useMemo(() => {
    if (!searchValue) return categoryOptions;

    return categoryOptions
      .map((group) => {
        if (!group) return null;

        return {
          ...group,
          options: (group.options || []).filter((item: any) => item?.label?.toLowerCase().includes(searchValue.toLowerCase()))
        };
      })
      .filter((item): item is NonNullable<typeof item> => item !== null && item.options.length > 0);
  }, [categoryOptions, searchValue]);

  useEffect(() => {
    const categoryId = form.getValues('categoryId');
    if (
      categoryId &&
      categoryOptions.length &&
      !categoryOptions.some((category) => category?.options?.some((item: any) => item.value === categoryId))
    ) {
      form.setValue('categoryId', null);
    }
  }, [categoryOptions, watchCountryCode]);

  useEffect(() => {
    if (!loadingMotPic && !loadingCompany && !loadingStatus && !loadingLocation && isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
      form.reset({
        ...defaultValues,
        location: defaultValues.location ? (Array.isArray(defaultValues.location) ? defaultValues.location : [defaultValues.location]) : null,
        departureFrom: DataInput.switchFormatDate(defaultValues.departureFrom) as Dayjs,
        departureTo: DataInput.switchFormatDate(defaultValues.departureTo) as Dayjs,
        createDateFrom: DataInput.switchFormatDate(defaultValues.createDateFrom) as Dayjs,
        createDateTo: DataInput.switchFormatDate(defaultValues.createDateTo) as Dayjs
      });
    }
  }, [loadingMotPic, loadingCompany, loadingStatus, loadingLocation]);

  useEffect(() => {
    if (userMotPicOptions?.length) {
      setMotPicOption([{ label: t('project:filter:not_yet_assigned'), value: '' }, ...userMotPicOptions]);
    }
  }, [userMotPicOptions]);

  const handleReset = () => {
    form.reset(isInternalRole ? DEFAULT_FILTER_FORM.primary : DEFAULT_FILTER_FORM_EXTERNAL.primary);
  };
  const disableResetBtn = () => {
    return !Object.values(form.getValues()).find(Boolean);
  };
  return (
    <>
      {!loadingMotPic && !loadingCompany && !loadingStatus && !loadingLocation && (
        <FormProvider {...form}>
          <div className='p-4 w-[600px] max-h-[810px] overflow-y-scroll'>
            <h3 className='text-xl font-semibold'>{t('project:list:filter_btn_label')}</h3>
            <div className='item mb-3'>
              <FormSelect
                name='countryIds'
                mode='multiple'
                label={t('project:filter:contry_of_travel:label')}
                options={customCountryOptions}
                placeholder={String(t('project:add:please_select_field', { field: t('project:filter:contry_of_travel:label') }))}
                filterOption={filterOption}
                handleChange={(value) => {
                  if (!value?.length) {
                    form.setValue('countryIds', null);
                    form.setValue('categoryId', null);
                  }
                }}
              />
            </div>
            <div className='item mb-3'>
              <FormSelect
                name='categoryId'
                label={t('project:filter:visa_category:label')}
                options={filteredCategoryOptions}
                disabled={!watchCountryCode?.length}
                placeholder={String(t('project:add:please_select_field', { field: t('project:filter:visa_category:label') }))}
                filterOption={false}
                onSearch={(val) => setSearchValue(val)}
                onDropdownVisibleChange={(open) => {
                  if (!open) {
                    setSearchValue('');
                  }
                }}
              />
            </div>
            {isInternalRole && (
              <>
                <div className='item mb-3'>
                  <FormSelect
                    name='picIds'
                    mode='multiple'
                    label={t('project:filter:mot_pick:label')}
                    options={motPicOption}
                    disabled={watchPicBy === TYPE_OF_TASK.ME}
                    placeholder={String(t('project:add:please_select_field', { field: t('project:filter:mot_pick:label') }))}
                    handleChange={(value) => {
                      if (!value?.length) {
                        form.setValue('picIds', null);
                      }
                    }}
                  />
                </div>
                <div className='item mb-3'>
                  <FormSelect
                    name='companyId'
                    label={t('basic_information:applicant_company')}
                    options={companyOptions}
                    placeholder={String(t('project:add:please_select_field', { field: t('basic_information:applicant_company') }))}
                  />
                </div>
              </>
            )}
            <div className='mb-3 flex items-center justify-between gap-4'>
              <FormSelect
                name='statuses'
                mode='multiple'
                label={t('project:fields:status')}
                options={statusData
                  ?.filter((item) => item.types?.includes(StatusCategory.PROJECT_LIST))
                  ?.map((item) => convertDataToOptions(item, { id: 'value', name: 'label' }))}
                placeholder={String(t('project:add:please_select_field', { field: t('project:filter:status:label') }))}
              />
            </div>
            <div className='mb-3 flex items-center justify-between gap-4'>
              <FormSelect
                name='location'
                mode='multiple'
                label={t('project:fields:location')}
                options={locationOptions}
                placeholder={String(t('project:add:please_select_field', { field: t('project:filter:location:label') }))}
                handleChange={(value) => {
                  if (!value?.length) {
                    form.setValue('location', null);
                  }
                }}
              />
            </div>
            <div className='mb-3 flex items-center justify-between gap-4'>
              <div className='w-[262px]'>
                <FormInputDate
                  name='departureFrom'
                  labelTx='project:filter:scheduled_travel_date:label'
                  placeholder={t('placeholder:date')}
                  disabledDate={(d) => {
                    return Boolean(departureTo && d.startOf('day').isAfter(departureTo));
                  }}
                />
              </div>
              <span className='w-[18px] h-[2px] mt-[35px] bg-[#525a68]'></span>
              <div className='w-[262px]'>
                <FormInputDate
                  name='departureTo'
                  label='&nbsp;'
                  placeholder={t('placeholder:date')}
                  disabledDate={(d) => {
                    return Boolean(departureFrom && d.endOf('day').isBefore(departureFrom));
                  }}
                />
              </div>
            </div>
            <div className='mb-3 flex items-center justify-between gap-4'>
              <div className='w-[262px]'>
                <FormInputDate
                  name='createDateFrom'
                  labelTx={t('project:filter:create_date:label')}
                  placeholderTx={t('placeholder:date')}
                  disabledDate={(d) => {
                    return Boolean(createDateTo && d.startOf('day').isAfter(createDateTo));
                  }}
                />
              </div>
              <span className='w-[18px] h-[2px] mt-[35px] bg-[#525a68]'></span>
              <div className='w-[262px]'>
                <FormInputDate
                  name='createDateTo'
                  label='&nbsp;'
                  placeholder={t('placeholder:date')}
                  disabledDate={(d) => {
                    return Boolean(createDateFrom && d.endOf('day').isBefore(createDateFrom));
                  }}
                />
              </div>
            </div>
            <div className='flex justify-end space-x-2'>
              <div className='w-[120px]'>
                <BaseButton onClick={handleReset} type='tertiary' size='medium' disabled={disableResetBtn()}>
                  {t('project:filter:btn_reset')}
                </BaseButton>
              </div>
              <div className='w-[120px]'>
                <BaseButton onClick={form.handleSubmit(onSubmit)} type='primary' size='medium'>
                  <span>{t('project:filter:btn_aply')} </span>
                </BaseButton>
              </div>
            </div>
          </div>
        </FormProvider>
      )}
    </>
  );
};

export default PrimaryFormFilter;
